import React, { useRef, useState, useEffect } from 'react';
import MapFrame from '../../components/MapFrame';
import RSVPForm       from '../../components/RSVPForm';
import Wishlist       from '../../components/Wishlist';
import YandexMap      from '../../components/YandexMap';
import Countdown      from '../../components/Countdown';
import TickerBar      from '../../components/TickerBar';
import FixedCTA       from '../../components/FixedCTA';
import useReveal      from '../../components/useReveal';
import Schedule       from '../../components/Schedule';
import DresscodePalette from '../../components/DresscodePalette';
import DateCards      from '../../components/DateCards';
import CalendarButton from '../../components/CalendarButton';
import BlockSubtitle  from '../../components/BlockSubtitle';
import PaidWatermark from '../../components/PaidWatermark';
import MusicPlayer from '../../components/MusicPlayer';
import './Ultramarin.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const src = u => !u ? null : (u.startsWith('http') ? u : u);
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

const fmtDateDots = raw => {
  if (!raw) return '??.??.????';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join('.');
};

/* Load Montserrat italic/bold/black weights once. */
function useMontserrat() {
  useEffect(() => {
    const id = 'um-montserrat-font';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,600;0,700;0,900;1,400;1,600;1,700;1,800;1,900&display=swap';
    document.head.appendChild(link);
  }, []);
}

/* ── Class maps for shared components ─────────────────────── */
const UM_TICKER_CLASSES = { bar: 'um-ticker', track: 'um-ticker-track', item: 'um-ticker-item', sep: 'um-ticker-sep' };
const UM_CD_CLASSES     = { root: 'um-cd',     cell: 'um-cd-cell', num: 'um-cd-num', label: 'um-cd-lbl' };
const UM_SCHED_CLASSES  = { root: 'um-sched',  row: 'um-sched-row', rowBorder: 'um-sched-row--border', time: 'um-sched-time', info: 'um-sched-info', name: 'um-sched-name', desc: 'um-sched-desc' };
const UM_DRESS_CLASSES  = { wrap: 'um-swatches', swatchWrap: 'um-swatch-wrap', swatch: 'um-swatch', swatchLabel: 'um-swatch-lbl', photos: 'um-dc-photos', photo: 'um-dc-photo' };
const UM_DATECARDS_CLASSES = { row: 'um-date-row', card: 'um-day-card', cardOn: 'um-day-card--on', num: 'um-day-num', mon: 'um-day-mon' };
const UM_RSVP_CLASSES = {
  form:    'um-form',
  tabs:    'um-rsvp-tabs',
  tab:     'um-rsvp-tab',
  tabSel:  'um-rsvp-tab--sel',
  field:   'um-form-field',
  label:   'um-form-lbl',
  input:   'um-form-input',
  submit:  'um-btn um-btn--primary um-btn--full',
  success: 'um-form-ok',
  error:   'um-form-err',
  chip:    'um-chip',
  chipSel: 'um-chip--sel',
  chips:   'um-form-chips',
};
const UM_WISHLIST_CLASSES = {
  list:        'um-wish',
  item:        'um-wish-item',
  itemBorder:  'um-wish-item--border',
  row:         'um-wish-row',
  name:        'um-wish-name',
  price:       'um-wish-price',
  btn:         'um-btn um-btn--primary um-btn--full',
  btnReserved: 'um-btn--res',
};
const UM_WISHLIST_MODAL_CLASSES = {
  bg:         'um-wish-modal-bg',
  modal:      'um-wish-modal',
  title:      'um-wish-modal-title',
  subtitle:   'um-wish-modal-sub',
  input:      'um-form-input',
  error:      'um-form-err',
  btns:       'um-wish-modal-btns',
  btnCancel:  'um-btn um-btn--outline',
  btnConfirm: 'um-btn um-btn--primary',
};

/* Инлайн декоративного SVG с перекраской (через <img> CSS-переменные не
   доходят, поэтому подменяем hex прямо в разметке). */
const _umSvgCache = {};
function UmDeco({ src, className, colors }) {
  const [raw, setRaw] = useState(() => (_umSvgCache[src] != null ? _umSvgCache[src] : null));
  useEffect(() => {
    if (_umSvgCache[src] != null) { setRaw(_umSvgCache[src]); return; }
    let alive = true;
    fetch(src).then(r => (r.ok ? r.text() : '')).then(t => { _umSvgCache[src] = t; if (alive) setRaw(t); }).catch(() => {});
    return () => { alive = false; };
  }, [src]);
  const cls = `um-svg${className ? ' ' + className : ''}`;
  if (!raw) return <span className={cls} aria-hidden="true" />;
  let html = raw;
  (colors || []).forEach(([from, to]) => { if (to && from !== to) html = html.split(from).join(to); });
  return <span className={cls} aria-hidden="true" dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ══ Main Template ═════════════════════════════════════════════ */
export default function Ultramarin({ project, design, isPreview }) {
  const pageRef  = useRef(null);
  const blockDep = project?.blocks?.length;
  useReveal(pageRef, blockDep, { revealSelector: '.um-reveal', inClass: 'um-in' });
  useMontserrat();

  const [inPreview, setInPreview] = useState(!!isPreview);
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    setInPreview(!!isPreview || !!root.closest('.lp-phone-screen,.lp-template-inner,[class*="lp-"]'));
  }, [isPreview]);

  const p = project || {};
  const d = design  || {};

  /* Colours — allow palette override but keep Ultramarin look as default. */
  const accent = d.palette?.primary   || p.palette?.primary   || '#2B00FF';
  const bg     = d.palette?.secondary || p.palette?.secondary || '#FFFBF2';
  const fg     = d.palette?.textDark  || p.palette?.textDark  || '#1E1E1E';
  const lime   = p.palette?.accent    || '#C7ED06';
  /* Декор всегда следует палитре: сердце — лайм-accent, пин — primary. */
  const heartColors = [['#C7ED06', lime]];
  const pinColors   = [['#2B00FF', accent]];
  const muted  = '#C6C8D2';

  /* Blocks */
  const blocks = p.blocks || ['menu','map','schedule','palette','rsvp','survey','message','wishlist','countdown','calendar'];
  const HAS_MENU     = blocks.includes('menu');
  const HAS_CALENDAR = blocks.includes('calendar');

  /* Text */
  const bride       = p.bride || '';
  const groom       = p.groom || '';
  const tagline     = p.block_content?.header?.subtitle || '';
  const footerQuote = p.block_content?.footer?.quote    || '';
  const invText     = p.invitation_text || p.block_content?.message?.text || '';
  const invTitle    = p.block_content?.message?.title || 'Дорогие наши!';

  /* Photos */
  const photos      = p.cover_photos || (p.cover_photo ? [{ url: p.cover_photo }] : []);
  const heroPhoto   = src(photos[0]?.url) || null;
  const heroPhotoMeta = photos[0] || null;
  const dcPhotos    = p.dresscode_photos || [];
  const footerPhotoObj = p.footer_photo?.url ? p.footer_photo : null;
  const footerPhoto    = src(footerPhotoObj?.url) || null;

  /* Date */
  const dateStr = fmtDateDots(p.wedding_date);

  /* Schedule */
  const schedule = p.schedule?.length ? p.schedule : [];

  /* Venues */
  const venues  = p.venues || [];
  const venue   = venues[0] || {};

  /* Dresscode */
  const swatches = p.dresscode_palette || [];

  /* Wishlist */
  const wishlist = p.wishlist || [];

  /* Block content titles */
  const bc = p.block_content || {};
  const titleSchedule = bc.schedule?.title  || '';
  const titleLocation = bc.map?.title       || '';
  const titleDress    = bc.palette?.title   || '';
  const titleRsvp     = bc.rsvp?.title      || '';
  const titleWish     = bc.wishlist?.title  || '';
  const titleCD       = bc.countdown?.title || '';
  const calBtn        = bc.calendar?.title  || '';

  return (
    <div className={`um-page${inPreview ? ' um-page--preview' : ''}`} ref={pageRef}
      style={{ '--um-accent': accent, '--um-bg': bg, '--um-fg': fg, '--um-lime': lime, '--um-muted': muted }}>
      <PaidWatermark isPaid={project?.is_paid} isPreview={inPreview} />

      {/* Ticker */}
      <TickerBar text={dateStr} classes={UM_TICKER_CLASSES} />

      {/* Navigation (Figma: nav at top:56px, before hero) */}
      {HAS_MENU && (
        <nav className="um-nav">
          {blocks.includes('schedule') && <a href="#um-schedule">{bc.menu?.schedule || 'Программа'}</a>}
          {blocks.includes('map')      && <a href="#um-location">{bc.menu?.location || 'Локация'}</a>}
          {blocks.includes('palette')  && <a href="#um-dresscode">{bc.menu?.dresscode || 'Дресс-код'}</a>}
          {blocks.includes('rsvp')     && <a href="#um-rsvp">{bc.menu?.rsvp || 'Анкета'}</a>}
        </nav>
      )}

      {/* Hero — names + photo */}
      <section className="um-hero um-reveal" data-nophoto={heroPhoto ? undefined : '1'}>
        <div className="um-hero-head">
          <div className="um-hero-names">
            {bride && <span className="um-hero-name">{bride}</span>}
            <span className="um-hero-amp">{bc.header?.amp || '&'}</span>
            {groom && <span className="um-hero-name">{groom}</span>}
          </div>
          {tagline && <p className="um-hero-tagline">{tagline}</p>}
        </div>
        {heroPhoto && (
          <div className="um-hero-photo">
            <img src={heroPhoto} alt="" style={{ filter: flt(heroPhotoMeta), objectPosition: objPos(heroPhotoMeta) }} />
          </div>
        )}
      </section>

      {/* Invitation letter */}
      <section className="um-pad um-invite um-reveal">
        <h2 className="um-invite-title">{invTitle}</h2>
        <div className="um-invite-body">
          {invText
            ? invText.split('\n').map((line, i) =>
                line.trim() ? <p key={i}>{line}</p> : <br key={i} />
              )
            : null
          }
        </div>
      </section>

      {/* Date cards + Calendar button, with decorative heart overlay */}
      <section className="um-pad um-date-sect um-reveal">
        <div className="um-date-wrap">
          <UmDeco className="um-date-heart" src="/templates/ultramarin/heart.svg" colors={heartColors} />
          <DateCards date={p.wedding_date} classes={UM_DATECARDS_CLASSES} />
        </div>
        {HAS_CALENDAR && (
          <CalendarButton
            bride={bride}
            groom={groom}
            date={p.wedding_date}
            time={p.wedding_time}
            venues={p.venues}
            address={p.wedding_address}
            className="um-btn um-btn--primary um-btn--full">
            {calBtn || 'Добавить в календарь'}
          </CalendarButton>
        )}
      </section>

      {/* Dynamic blocks — Figma order */}
      {['map','schedule','palette','rsvp','wishlist','countdown']
        .filter(b => blocks.includes(b))
        .map(blockId => {
          switch (blockId) {
            case 'map':
              return (
                <section key="map" className="um-map-sect um-reveal" id="um-location">
                  <div className="um-pad">
                    <h2 className="um-h">{titleLocation || 'Локация'}</h2>
                    <BlockSubtitle text={bc.map?.subtitle} className="um-p" />
                    {venues.map((v, i) => (
                      <div key={i} className="um-addr">
                        {(v.address || '').trim() && <UmDeco className="um-addr-icon" src="/templates/ultramarin/location-pin.svg" colors={pinColors} />}
                        <span className="um-addr-text">
                          {v.name && v.address ? `${v.name}, ${v.address}` : (v.name || v.address)}
                        </span>
                      </div>
                    ))}
                    {!venues.some(v => v.address) && p.wedding_address && (
                      <div className="um-addr">
                        <UmDeco className="um-addr-icon" src="/templates/ultramarin/location-pin.svg" colors={pinColors} />
                        <span className="um-addr-text">{p.wedding_address}</span>
                      </div>
                    )}
                  </div>
                  {/* Карта — только при наличии адреса (имя без адреса → без карты). */}
                  {(p.map_url || (p.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
                  <div className="um-map-wrap">
                    {(() => {
                      if (p.map_url) {
                        return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={p.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                      }
                      const withCoords = venues.filter(v => Number.isFinite(+v.lat) && Number.isFinite(+v.lon));
                      if (withCoords.length > 0) {
                        return <YandexMap points={withCoords} singleZoom={16} region={p.region} provider={bc.map?.provider} preset="islands#blueIcon" />;
                      }
                      const addr = venues.length > 0
                        ? venues.map(v => v.address).filter(Boolean).join(' — ')
                        : (p.wedding_address || '');
                      if (!addr) return null;
                      const s = `https://yandex.ru/map-widget/v1/?mode=search&text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                      return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={s} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                    })()}
                  </div>
                  )}
                </section>
              );

            case 'schedule':
              return (
                <section key="schedule" className="um-pad um-sched-sect um-reveal" id="um-schedule">
                  <h2 className="um-h">{titleSchedule || 'Программа'}</h2>
                  <BlockSubtitle text={bc.schedule?.subtitle} className="um-p" />
                  <Schedule items={schedule} classes={UM_SCHED_CLASSES} />
                </section>
              );

            case 'palette':
              return (
                <section key="palette" className="um-pad um-dress-sect um-reveal" id="um-dresscode">
                  <h2 className="um-h">{titleDress || 'Дресс-код'}</h2>
                  <BlockSubtitle text={bc.palette?.subtitle} className="um-p" />
                  <DresscodePalette
                    swatches={swatches}
                    photos={dcPhotos}
                    cssFilter={flt}
                    classes={UM_DRESS_CLASSES}
                  />
                </section>
              );

            case 'rsvp':
              return (
                <section key="rsvp" className="um-pad um-rsvp-sect um-reveal" id="um-rsvp">
                  <h2 className="um-h">{titleRsvp || 'Подтвердите присутствие'}</h2>
                  <BlockSubtitle text={bc.rsvp?.subtitle} className="um-p" />
                  <RSVPForm project={p} blocks={blocks} classes={UM_RSVP_CLASSES} />
                </section>
              );

            case 'wishlist':
              return (
                <section key="wishlist" className="um-pad um-wish-sect um-reveal">
                  <h2 className="um-h">{titleWish || 'Что дарить'}</h2>
                  <BlockSubtitle text={bc.wishlist?.subtitle} className="um-p" />
                  <Wishlist
                    items={wishlist} currency={bc.wishlist?.currency} reserveText={bc.wishlist?.reserveText}
                    slug={p.slug}
                    classes={UM_WISHLIST_CLASSES}
                    modalClasses={UM_WISHLIST_MODAL_CLASSES}
                  />
                </section>
              );

            case 'countdown':
              return (
                <section key="countdown" className="um-pad um-cd-sect um-reveal">
                  <h2 className="um-h">{titleCD || 'До нашей свадьбы'}</h2>
                  <BlockSubtitle text={bc.countdown?.subtitle} className="um-p" />
                  <Countdown date={p.wedding_date} time={p.wedding_time} bc={bc} classes={UM_CD_CLASSES} />
                </section>
              );

            default:
              return null;
          }
        })}

      {/* Footer — photo strip + giant quote panel */}
      <footer className="um-footer um-reveal">
        {footerPhoto && (
          <div className="um-footer-photo">
            <img src={footerPhoto} alt="" style={{ filter: flt(footerPhotoObj), objectPosition: objPos(footerPhotoObj) }} />
          </div>
        )}
        <div className="um-footer-quote">
          <p>{(footerQuote || 'Одна любовь, другой такой и не было').split('\n').map((line, i, arr) => (
            <React.Fragment key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}</p>
        </div>
      </footer>

      {/* Fixed RSVP CTA */}
      {blocks.includes('rsvp') && (
        <FixedCTA
          href="#um-rsvp"
          targetId="um-rsvp"
          scrollRoot={pageRef}
          className="um-fixed-cta">
          {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
        </FixedCTA>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={inPreview} />
      )}
    </div>
  );
}
